/**
 * The file archive engine of one open project (docs/specs/cloud/file-archive.md §3; MB decisions 102, 376; sign-off
 * 06-054). The project database queues every imported and exported file (teslyn-db migration 0023, behind
 * {@link ArchiveQueue}); this engine sends each one to Teslyn Cloud (Edge Function archive-upload, behind
 * {@link ArchiveSender}), which puts it in the tenant's archive: Teslyn Cloud storage through a signed upload
 * address, or the tenant's own Nextcloud, forwarded with the app password Teslyn Cloud keeps in Vault. The laptop
 * never holds that password. Nothing here needs the network: a failed send stays queued with a time to try again.
 */
import { nextcloudPieces, type ArchiveDestination, type ArchiveSource } from '@teslyn/domain';
import type { LocalStatus } from './engine.js';
import { retryAt, sha256Hex } from './files.js';
import { NIL_TENANT } from './tables.js';

/** A file to archive (teslyn-db `ArchiveJob`). */
export interface ArchiveJob {
  readonly id: string;
  readonly project_id: string;
  readonly source: ArchiveSource;
  readonly local_path: string;
  readonly file_name: string;
  /** The laptop's date of the import or export (yyyy-mm-dd). */
  readonly day: string;
  /** As queued, when known (a flight log's are read when it is sent). */
  readonly size_bytes: number | null;
  readonly sha256: string | null;
  readonly state: 'pending' | 'done' | 'refused' | 'missing' | 'skipped';
  readonly attempts: number;
  /**
   * More fields Teslyn Cloud is told with every request of this file, as strings (the processors' zips: `purpose`,
   * `source: processor_zip`, `report_id`, `report_revision`, `project_folder` …; R165, processor-zip.ts). They are
   * sent after the file's own fields, so they may replace `source`. None for an archived file.
   */
  readonly extra?: Readonly<Record<string, string>>;
}

/** The outcome of one file (teslyn-db `ArchiveAck`). */
export type ArchiveAck =
  | {
      readonly outcome: 'done';
      readonly id: string;
      readonly destination: ArchiveDestination;
      readonly remote_path: string;
      readonly sha256: string;
      readonly size_bytes: number;
    }
  | {
      readonly outcome: 'retry';
      readonly id: string;
      readonly code: string;
      readonly message: string;
      readonly retry_at: string;
    }
  | { readonly outcome: 'refused'; readonly id: string; readonly code: string; readonly message: string }
  | { readonly outcome: 'missing'; readonly id: string }
  /** Archiving was switched off for the tenant (decision 393): left out, never offered again. */
  | { readonly outcome: 'skipped'; readonly id: string; readonly code: string; readonly message: string };

/** Counts and the last outcome (teslyn-db `ArchiveInfo`). */
export interface ArchiveInfo {
  readonly waiting: number;
  readonly failed: number;
  readonly archived: number;
  readonly skipped: number;
  readonly last_error: string | null;
  readonly last_done_at: string | null;
  readonly last_destination: ArchiveDestination | null;
}

/** The project database's archive queue (Tauri commands archive_* in Field; teslyn-db src/archive.rs). */
export interface ArchiveQueue {
  status(): Promise<LocalStatus>;
  archivePending(limit: number, now: string): Promise<ArchiveJob[]>;
  archiveAck(acks: readonly ArchiveAck[]): Promise<void>;
  /** The file as it is now (size and SHA-256), or null when it is no longer there. */
  archiveStat(job: ArchiveJob): Promise<{ readonly size_bytes: number; readonly sha256: string } | null>;
  /** `length` bytes of the file from `offset`. */
  archiveRead(job: ArchiveJob, offset: number, length: number): Promise<Uint8Array>;
}

/** One file as Teslyn Cloud is told about it. */
export interface ArchiveFile {
  readonly tenant_id: string;
  readonly project_id: string;
  readonly file_name: string;
  readonly day: string;
  readonly size_bytes: number;
  readonly sha256: string;
  readonly source: ArchiveSource;
}

export interface ArchiveDone {
  readonly state: 'done';
  readonly destination: ArchiveDestination;
  readonly path: string;
  readonly name: string;
}

export type ArchiveBegin =
  | ArchiveDone
  | {
      readonly state: 'upload';
      readonly mode: 'signed';
      readonly destination: 'survera';
      readonly path: string;
      readonly name: string;
      readonly upload_url: string;
    }
  | {
      readonly state: 'upload';
      readonly mode: 'single' | 'chunked';
      readonly destination: 'nextcloud';
      readonly path: string;
      readonly name: string;
      readonly pieces: number;
    };

/** Teslyn Cloud's archive-upload, as the signed-in user. */
export interface ArchiveSender {
  begin(f: ArchiveFile): Promise<ArchiveBegin>;
  /** A Nextcloud file up to one piece, whole. */
  put(f: ArchiveFile, name: string, bytes: Uint8Array): Promise<ArchiveDone>;
  /** One 8 MiB piece of a larger Nextcloud file. */
  piece(f: ArchiveFile, name: string, n: number, bytes: Uint8Array): Promise<void>;
  finish(f: ArchiveFile, name: string): Promise<ArchiveDone>;
  /** The whole file to Teslyn Cloud storage's signed upload address. */
  putSigned(url: string, bytes: Uint8Array): Promise<void>;
}

/**
 * - `offline`: no answer; `server`: Teslyn Cloud failed; `nextcloud`: the tenant's Nextcloud refused or could not be
 *   reached (`nextcloud` holds its code): each stops the run, as every other file would fail the same way;
 * - `denied`: the project is not in Teslyn Cloud yet, or the role may not archive: this file waits;
 * - `refused`: never as it is (too large, empty);
 * - `off`: the tenant switched archiving off (decision 393): this file is left out.
 */
export type ArchiveSendErrorCode = 'offline' | 'server' | 'nextcloud' | 'denied' | 'refused' | 'off';

export class ArchiveSendError extends Error {
  constructor(
    readonly code: ArchiveSendErrorCode,
    message: string,
    /** Teslyn Cloud's code (too_large, no_project, …) or the Nextcloud's (wrong_password, quota_full, …). */
    readonly detail: string | null = null,
  ) {
    super(message);
    this.name = 'ArchiveSendError';
  }
}

export interface ArchiveReport {
  readonly state: 'done' | 'needs_link' | 'retry_later';
  readonly archived: number;
  /** Already in the archive with the same content. */
  readonly unchanged: number;
  readonly refused: number;
  readonly missing: number;
  readonly retried: number;
  /** Left out: archiving was switched off. */
  readonly skipped: number;
}

const BATCH = 10;
const MAX_ROUNDS = 20;

type Counted = keyof Omit<ArchiveReport, 'state'>;
type Outcome = { readonly ack: ArchiveAck; readonly counted: Counted; readonly stop: boolean };

export class ArchiveUploadEngine {
  constructor(
    private readonly queue: ArchiveQueue,
    private readonly sender: ArchiveSender,
    private readonly clock: () => Date = () => new Date(),
  ) {}

  /** Send every file that is due, in batches, and record the outcomes. */
  async run(): Promise<ArchiveReport> {
    const report = { archived: 0, unchanged: 0, refused: 0, missing: 0, retried: 0, skipped: 0 };
    const status = await this.queue.status();
    const tenantId = status.tenant_id && status.tenant_id !== NIL_TENANT ? status.tenant_id : null;
    if (!tenantId) return { state: 'needs_link', ...report };
    for (let round = 0; round < MAX_ROUNDS; round++) {
      const jobs = await this.queue.archivePending(BATCH, this.clock().toISOString());
      if (jobs.length === 0) return { state: 'done', ...report };
      for (const job of jobs) {
        const o = await this.one(job, tenantId);
        await this.queue.archiveAck([o.ack]);
        report[o.counted] += 1;
        if (o.stop) return { state: 'retry_later', ...report };
      }
    }
    return { state: 'retry_later', ...report };
  }

  private async one(job: ArchiveJob, tenantId: string): Promise<Outcome> {
    const now = await this.queue.archiveStat(job);
    if (!now) return { ack: { outcome: 'missing', id: job.id }, counted: 'missing', stop: false };
    const f: ArchiveFile = {
      tenant_id: tenantId,
      project_id: job.project_id,
      file_name: job.file_name,
      day: job.day,
      size_bytes: now.size_bytes,
      sha256: now.sha256,
      source: job.source,
      // last, so a processors' zip says `source: processor_zip`: an archive-upload older than R165 then refuses it
      // ("source must be 'import' or 'export'") and the zip waits, instead of being archived as a day's file
      ...job.extra,
    };
    const done = (d: ArchiveDone, counted: Counted): Outcome => ({
      ack: {
        outcome: 'done',
        id: job.id,
        destination: d.destination,
        remote_path: d.path,
        sha256: now.sha256,
        size_bytes: now.size_bytes,
      },
      counted,
      stop: false,
    });
    try {
      const begin = await this.sender.begin(f);
      if (begin.state === 'done') return done(begin, 'unchanged');
      let d: ArchiveDone;
      if (begin.mode === 'signed') {
        await this.sender.putSigned(begin.upload_url, await this.queue.archiveRead(job, 0, now.size_bytes));
        d = await this.sender.finish(f, begin.name);
      } else if (begin.mode === 'single') {
        d = await this.sender.put(f, begin.name, await this.queue.archiveRead(job, 0, now.size_bytes));
      } else {
        for (const p of nextcloudPieces(now.size_bytes)) {
          await this.sender.piece(f, begin.name, p.n, await this.queue.archiveRead(job, p.offset, p.length));
        }
        d = await this.sender.finish(f, begin.name);
      }
      return done(d, 'archived');
    } catch (e) {
      if (!(e instanceof ArchiveSendError)) throw e;
      if (e.code === 'off') {
        return {
          ack: { outcome: 'skipped', id: job.id, code: e.detail ?? 'archive_off', message: e.message },
          counted: 'skipped',
          stop: false,
        };
      }
      if (e.code === 'refused') {
        return {
          ack: { outcome: 'refused', id: job.id, code: e.detail ?? 'refused', message: e.message },
          counted: 'refused',
          stop: false,
        };
      }
      return {
        ack: {
          outcome: 'retry',
          id: job.id,
          code: e.detail ?? e.code,
          message: e.message,
          retry_at: retryAt(this.clock(), job.attempts),
        },
        counted: 'retried',
        stop: e.code !== 'denied',
      };
    }
  }
}

/** A file held in memory, archived at once (Teslyn Cloud's browser, where nothing is queued on disk). */
export interface ArchiveNow {
  readonly tenantId: string;
  readonly projectId: string;
  readonly fileName: string;
  /** The day folder (yyyy-mm-dd): the day the file was added. */
  readonly day: string;
  readonly bytes: Uint8Array;
  readonly source?: ArchiveSource;
}

/**
 * The archive queue of one file in memory, so Teslyn Cloud archives a photo staff add (decision 431) through the
 * same engine and Edge Function as Teslyn Field. The outcome is the engine's acknowledgement.
 */
export class MemoryArchiveQueue implements ArchiveQueue {
  private ack: ArchiveAck | null = null;
  private stat: { size_bytes: number; sha256: string } | null = null;

  constructor(private readonly file: ArchiveNow) {}

  /** What happened to the file, once {@link ArchiveUploadEngine.run} has returned. */
  get outcome(): ArchiveAck | null {
    return this.ack;
  }

  async status(): Promise<LocalStatus> {
    return {
      device_id: 'browser',
      db_id: 'browser',
      tenant_id: this.file.tenantId,
      pending: 0,
      refused: 0,
      blocked: 0,
    };
  }

  async archivePending(): Promise<ArchiveJob[]> {
    if (this.ack) return [];
    return [
      {
        id: `cloud:${this.file.projectId}:${this.file.fileName}`,
        project_id: this.file.projectId,
        source: this.file.source ?? 'import',
        local_path: this.file.fileName,
        file_name: this.file.fileName,
        day: this.file.day,
        size_bytes: this.file.bytes.byteLength,
        sha256: null,
        state: 'pending',
        attempts: 0,
      },
    ];
  }

  async archiveAck(acks: readonly ArchiveAck[]): Promise<void> {
    this.ack = acks[acks.length - 1] ?? this.ack;
  }

  async archiveStat(): Promise<{ readonly size_bytes: number; readonly sha256: string }> {
    this.stat ??= { size_bytes: this.file.bytes.byteLength, sha256: await sha256Hex(this.file.bytes) };
    return this.stat;
  }

  async archiveRead(_job: ArchiveJob, offset: number, length: number): Promise<Uint8Array> {
    return this.file.bytes.subarray(offset, offset + length);
  }
}

/** Archive one file now; the engine's acknowledgement says how it went (done, retry, refused, skipped). */
export async function archiveNow(
  file: ArchiveNow,
  sender: ArchiveSender,
  clock: () => Date = () => new Date(),
): Promise<ArchiveAck> {
  const queue = new MemoryArchiveQueue(file);
  await new ArchiveUploadEngine(queue, sender, clock).run();
  return queue.outcome ?? { outcome: 'missing', id: 'cloud' };
}
